import { Component, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { webEnums } from '@vectronic/openApi-customer';
import { CardComponent, ConfirmationDialogComponent } from '@vectronic/ui';
import { finalize, Observable } from 'rxjs';
import { DatatableComponent } from '../../components/datatable/datatable.component';
import { DatatableConfig } from '../../types/datatable-configuration';

type ManageNetworkstatusDialogData = {
  deviceIds: number[];
  comType?: webEnums.ComType;
  hasGsmSelection?: boolean;
  allGsmSelection?: boolean;
  currentNetworkStatus?: Record<number, webEnums.NetworkStatus>;
  requestedNetworkStatusChange?: Record<number, webEnums.AirtimeNetworkStatusChangeRequested>;
  devicePlans?: Record<number, webEnums.DataPlan | string>;
  activate$?: (ids: number[]) => Observable<any>;
  confirmActivate$?: (ids: number[]) => Observable<any>;
  deactivate$?: (ids: number[]) => Observable<any>;
  confirmDeactivate$?: (ids: number[]) => Observable<any>;
};

type PlanSummaryRow = { plan: string; ids: number[]; idsLabel: string; count: number };

@Component({
  selector: 'table-manage-networkstatus-dialog',
  templateUrl: './manage-networkstatus-dialog.component.html',
  styleUrls: ['./manage-networkstatus-dialog.component.scss'],
  imports: [
    CardComponent,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatSelectModule,
    DatatableComponent,
  ],
})
export class ManageNetworkstatusDialogComponent {
  private matDialog = inject(MatDialog);

  dialogRef = inject(MatDialogRef<ManageNetworkstatusDialogComponent>);
  data: ManageNetworkstatusDialogData = inject(MAT_DIALOG_DATA);

  isLoading = signal(false);
  errorMessage = signal<string | null>(null);

  selectedAction = signal<'activate' | 'deactivate' | undefined>(undefined);

  constructor() {
    // Keep this dialog open unless closed explicitly via its own actions.
    this.dialogRef.disableClose = true;
  }

  title = computed(() => {
    if (this.showActivate() && !this.showDeactivate()) {
      return 'Activate Airtime Service';
    }
    if (!this.showActivate() && this.showDeactivate()) {
      return 'Deactivate Airtime Service';
    }
    return 'Manage Network Status';
  });
  canSubmit = computed(() => {
    if (this.isLoading()) return false;
    const both = this.showActivate() && this.showDeactivate();
    return both ? !!this.selectedAction() : true;
  });

  showActivate = computed(() => {
    const current = this.data?.currentNetworkStatus ?? {};
    const requested = this.data?.requestedNetworkStatusChange ?? {};
    const hasHandler = typeof this.data?.activate$ === 'function';
    if (!hasHandler) return false;
    const ids = this.data?.deviceIds ?? [];
    return ids.some((id) => current[id] !== 'ACTIVATED' && !requested[id]);
  });

  showDeactivate = computed(() => {
    const current = this.data?.currentNetworkStatus ?? {};
    const requested = this.data?.requestedNetworkStatusChange ?? {};
    const hasHandler = typeof this.data?.deactivate$ === 'function';
    if (!hasHandler) return false;
    const ids = this.data?.deviceIds ?? [];
    return ids.some((id) => current[id] === 'ACTIVATED' && !requested[id]);
  });

  hasGsmSelection = computed(
    () => this.data?.hasGsmSelection === true || this.data?.comType === 'GSM',
  );

  allGsmSelection = computed(
    () => this.data?.allGsmSelection === true || this.data?.comType === 'GSM',
  );

  showGsmDeactivationWarning = computed(() => {
    const onlyDeactivateAvailable = !this.showActivate() && this.showDeactivate();
    const deactivateSelectedInDropdown =
      this.showActivate() && this.showDeactivate() && this.selectedAction() === 'deactivate';
    return this.hasGsmSelection() && (onlyDeactivateAvailable || deactivateSelectedInDropdown);
  });

  private buildDeactivationConfirmText() {
    const com = this.data?.comType;
    const isGlobalstar = com === 'GLOBALSTAR';
    const isIridium = com === 'IRIDIUM';
    const isGsm = this.hasGsmSelection();

    const ids = this.data?.deviceIds ?? [];
    const devicesLabel = `device(s) ${ids.join(', ')}`;

    const lines: string[] = [];
    lines.push(`Deactivating airtime for ${devicesLabel} will result in the following:`);
    lines.push(`• ${isGsm ? 'Network' : 'Satellite'} service will be disabled`);
    lines.push('• No further monthly service fees will be charged');

    if (isGlobalstar || isIridium) {
      lines.push('• If reactivated later, a reactivation fee will apply');
    }
    if (isGsm) {
      lines.push('• Reactivating airtimes for GSM is not possible');
    }

    if (isGlobalstar) {
      lines.push('• Deactivation requests may take up to 1 business day to complete');
    }

    return lines.join('\n');
  }

  networkStatusLabel(status?: webEnums.NetworkStatus) {
    switch (status) {
      case 'ACTIVATING':
        return 'Activating';
      case 'ACTIVATED':
        return 'Activated';
      case 'DEACTIVATING':
        return 'Deactivating';
      case 'DEACTIVATED':
        return 'Deactivated';
      case 'SUSPENDING':
        return 'Suspending';
      case 'RESUMING':
        return 'Resuming';
      case 'CHANGING':
        return 'Changing';
      case 'CHANGED':
        return 'Changed';
      case 'INVALID':
        return 'Invalid';
      case undefined:
      case null:
      default:
        return 'No network status';
    }
  }

  planSummaryRows = computed<PlanSummaryRow[]>(() => {
    const ids = this.data?.deviceIds ?? [];
    const currentNetworkStatus = this.data?.currentNetworkStatus ?? {};
    const requested = this.data?.requestedNetworkStatusChange ?? {};
    const planToIds = new Map<string, number[]>();
    for (const id of ids) {
      const hasRequested = id in requested;
      const label = hasRequested
        ? 'Activation Requested'
        : this.networkStatusLabel(currentNetworkStatus[id]);
      const list = planToIds.get(label) ?? [];
      list.push(id);
      planToIds.set(label, list);
    }
    return Array.from(planToIds.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([plan, ids]) => ({ plan, ids, idsLabel: ids.join(', '), count: ids.length }));
  });

  planTableConfig: DatatableConfig<PlanSummaryRow> = {
    uniqueId: 'activate-airtimes-plan-summary',
    title: '',
    columns: [
      { columnId: 1, fieldName: 'plan', headerName: 'Current Network Status', disableSort: true },
      {
        columnId: 2,
        fieldName: 'count',
        headerName: 'Number of devices',
        disableSort: true,
        cellHoverText: (row: PlanSummaryRow) => row?.idsLabel,
      },
    ],
    disablePagination: true,
    disableEditColumns: true,
    hideHeader: true,
  };

  deviceIdsLabel = computed(() => (this.data?.deviceIds ?? []).join(', '));

  isSingleSelection = computed(() => (this.data?.deviceIds?.length ?? 0) === 1);

  private planDisplayName(plan: webEnums.DataPlan | string): string {
    const p = String(plan);
    switch (p) {
      case 'UNLIMITED':
        return 'Unlimited';
      case 'THREE_KBYTE':
        return '3 kB';
      case 'POOLED_THREE_KBYTE':
        return 'Pooled 3 kB';
      case 'POOLED_TWELVE_KBYTE':
        return 'Pooled 12 kB';
      case 'SMITHSONIAN_SPECIAL':
        return 'Smithsonian Special';
      case 'TEST_2_5_KBYTE':
        return 'Test 2.5 kB';
      case 'TEST_4_KBYTE':
        return 'Test 4 kB';
      default: {
        return p
          .toLowerCase()
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
      }
    }
  }

  private serviceLabel(com?: webEnums.ComType): string {
    if (com === 'GLOBALSTAR') return 'Globalstar';
    if (com === 'IRIDIUM') return 'Iridium';
    return 'service';
  }

  singleActivationHeader = computed(() => {
    const ids = this.data?.deviceIds ?? [];
    const idsLabel = ids.join(', ');
    const com = this.data?.comType;
    const service = this.serviceLabel(com);

    let planSuffix = '';
    if (com === 'IRIDIUM') {
      const plansMap = this.data?.devicePlans ?? {};
      const plans = ids
        .map((id) => plansMap[id])
        .filter((p) => p != null)
        .map((p) => String(p));
      if (plans.length > 0) {
        const unique = Array.from(new Set(plans));
        if (unique.length === 1) {
          planSuffix = ` under the ${this.planDisplayName(unique[0])} plan`;
        } else {
          planSuffix = ' under multiple plans';
        }
      }
    }

    return `Are you sure you want to (re)activate ${service} for device(s) ${idsLabel}${planSuffix}?`;
  });

  singleActivationInfo = computed(() => {
    const ids = this.data?.deviceIds ?? [];
    const idsLabel = ids.join(', ');
    const com = this.data?.comType;
    const isGlobalstar = com === 'GLOBALSTAR';
    const sendReceive = isGlobalstar ? 'send' : 'send and receive';
    const networkWording = com === 'GSM' ? 'network' : 'satellite';
    return `• The device(s) ${idsLabel} will be able to ${sendReceive} data via ${networkWording}.`;
  });

  singleActivationGlobalstar = computed(() => {
    const com = this.data?.comType;
    const isGlobalstar = com === 'GLOBALSTAR';
    if (isGlobalstar) {
      return '• Activation requests may take up to 1 business day to complete.';
    }
    return '';
  });

  singleDeactivationHeader = computed(() => {
    const ids = this.data?.deviceIds ?? [];
    const idsLabel = ids.join(', ');
    const com = this.data?.comType;
    const service = this.serviceLabel(com);
    return `Are you sure you want to deactivate ${service} for device(s) ${idsLabel}?`;
  });

  singleDeactivationInfo = computed(() => {
    const ids = this.data?.deviceIds ?? [];
    const idsLabel = ids.join(', ');
    const com = this.data?.comType;
    const isGlobalstar = com === 'GLOBALSTAR';
    const sendReceive = isGlobalstar ? 'send' : 'send and receive';
    const networkWording = com === 'GSM' ? 'network' : 'satellite';
    return `• The device(s) ${idsLabel} will no longer be able to ${sendReceive} data via ${networkWording}.`;
  });

  close() {
    if (this.isLoading()) return;
    this.dialogRef.close();
  }

  submitSelected() {
    const action = this.selectedAction();
    if (action === 'activate') {
      this.onActivate();
    } else if (action === 'deactivate') {
      this.onDeactivate();
    }
  }

  private executeRequest<T>(
    request$: Observable<T>,
    onSuccess: () => void,
    onError: (error: any) => void,
  ) {
    this.isLoading.set(true);
    request$
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe({ next: () => onSuccess(), error: onError });
  }

  onActivate() {
    this.errorMessage.set(null);

    const currentNetworkStatus = this.data?.currentNetworkStatus ?? {};
    const requestedNetworkStatusChange = this.data?.requestedNetworkStatusChange ?? {};
    const ids = (this.data.deviceIds ?? []).filter(
      (id) => currentNetworkStatus[id] !== 'ACTIVATED' && !requestedNetworkStatusChange[id],
    );

    this.executeRequest(
      this.data.activate$(ids),
      () => this.dialogRef.close({ success: true, ids }),
      (err: any) => {
        const msg = (err?.error?.message as string) || (err?.message as string) || null;
        if (msg && (/one-time activation fee/i.test(msg) || /Monthly service fee/i.test(msg))) {
          const confirmRef = this.matDialog.open(ConfirmationDialogComponent, {
            data: { title: '(Re)Activation Fee Applies', message: msg },
          });
          confirmRef.afterClosed().subscribe((confirmed: boolean) => {
            if (confirmed) {
              this.executeRequest(
                this.data.confirmActivate$(ids),
                () => this.dialogRef.close({ success: true, ids }),
                (e: any) => {
                  const em =
                    (e?.error?.message as string) || (e?.message as string) || 'Activation failed';
                  this.errorMessage.set(em);
                },
              );
            }
          });
          this.errorMessage.set(null);
        } else {
          this.errorMessage.set(msg || 'Activation failed');
        }
      },
    );
  }

  onDeactivate() {
    this.errorMessage.set(null);

    const currentNetworkStatus = this.data?.currentNetworkStatus ?? {};
    const requestedNetworkStatusChange = this.data?.requestedNetworkStatusChange ?? {};
    const ids = (this.data.deviceIds ?? []).filter(
      (id) => currentNetworkStatus[id] === 'ACTIVATED' && !requestedNetworkStatusChange[id],
    );

    this.executeRequest(
      this.data.deactivate$(ids),
      () => this.dialogRef.close({ success: true, ids }),
      (err: any) => {
        const msg = (err?.error?.message as string) || (err?.message as string) || null;
        if (msg && /Please confirm/i.test(msg)) {
          const confirmRef = this.matDialog.open(ConfirmationDialogComponent, {
            data: {
              title: 'Deactivation Consequences',
              message: `${this.buildDeactivationConfirmText()}`,
              hideCancel: true,
            },
          });
          confirmRef.afterClosed().subscribe((confirmed: boolean) => {
            if (confirmed) {
              this.executeRequest(
                this.data.confirmDeactivate$(ids),
                () => this.dialogRef.close({ success: true, ids }),
                (e: any) => {
                  const em =
                    (e?.error?.message as string) ||
                    (e?.message as string) ||
                    'Deactivation failed';
                  this.errorMessage.set(em);
                },
              );
            }
          });
          this.errorMessage.set(null);
        } else {
          this.errorMessage.set(msg || 'Deactivation failed');
        }
      },
    );
  }
}
